/**
 * Phase 7 / BR-34: Dismissible info banners.
 * useDismissibleBanner(bannerId) => [isDismissed, dismissBanner, loading]
 */
import { useState, useEffect, useCallback } from "react";
import { getPreferences, dismissBanner as apiDismissBanner } from "../services/usersService";

export function useDismissibleBanner(
  bannerId: string
): [boolean, (id?: string) => Promise<void>, boolean] {
  const [isDismissed, setIsDismissed] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;
    setLoading(true);
    getPreferences()
      .then((res) => {
        if (!mounted) return;
        const list = res.data?.preferences?.dismissed_banners ?? [];
        setIsDismissed(Array.isArray(list) && list.includes(bannerId));
      })
      .catch(() => {
        if (mounted) setIsDismissed(false);
      })
      .finally(() => {
        if (mounted) setLoading(false);
      });
    return () => {
      mounted = false;
    };
  }, [bannerId]);

  const dismissBanner = useCallback(
    async (_bannerId?: string) => {
      const id = _bannerId ?? bannerId;
      try {
        await apiDismissBanner(id);
        setIsDismissed(true);
      } catch (e) {
        console.error("Failed to dismiss banner:", e);
      }
    },
    [bannerId]
  );

  return [isDismissed, dismissBanner, loading];
}
